import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from '../contexts/TranslationContext';
import { Users, TrendingUp, Calendar, DollarSign, Eye, UserPlus, Plus, Edit, Trash2, X, Loader2, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import apiService from '../utils/apiService';

const SubAffiliateCampaigns = () => {
  const { isDarkMode } = useTheme();
  useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // API data state
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false
  });
  
  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  
  // Edit state
  const [showEditModal, setShowEditModal] = useState(false);
  const [campaignToEdit, setCampaignToEdit] = useState(null);
  const [editCampaignName, setEditCampaignName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editError, setEditError] = useState('');
  
  // Prevent double API call
  const apiCallMade = useRef(false);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  // Fetch campaigns from API
  const fetchCampaigns = async (pageNumber = 1, pageSize = pagination.pageSize, sort = sortBy, order = sortOrder) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiService.getSubCampaignsList({
        pageNumber: pageNumber,
        pageSize: pageSize,
        sortBy: sort,
        sortOrder: order
      });
      
      if (response.success && response.data) {
        setCampaigns(response.data.data || []);
        setPagination({
          pageNumber: response.data.pageNumber || 1,
          pageSize: response.data.pageSize || 10,
          totalRecords: response.data.totalRecords || 0,
          totalPages: response.data.totalPages || 0,
          hasPreviousPage: response.data.hasPreviousPage || false,
          hasNextPage: response.data.hasNextPage || false
        });
      } else {
        setError(response.message || 'Failed to fetch campaigns');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch campaigns');
    } finally {
      setIsLoading(false);
      setIsLoaded(true);
    }
  };

  // Delete campaign
  const handleDeleteCampaign = async () => {
    if (!campaignToDelete) return;
    
    setIsDeleting(true);
    setDeleteError('');
    try {
      const response = await apiService.deleteSubCampaign(campaignToDelete.campaignID);
      if (response.success) {
        setShowDeleteModal(false);
        setCampaignToDelete(null);
        fetchCampaigns();
      } else {
        setDeleteError(response.message || 'Failed to delete campaign');
      }
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete campaign');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle sort
  const handleSort = (column) => {
    let newOrder = 'asc';
    if (sortBy === column) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSortBy(column);
    setSortOrder(newOrder);
    fetchCampaigns(1, pagination.pageSize, column, newOrder);
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <ChevronsUpDown className="h-3 w-3 ml-1 opacity-50" />;
    }
    return sortOrder === 'asc' 
      ? <ChevronUp className="h-3 w-3 ml-1" />
      : <ChevronDown className="h-3 w-3 ml-1" />;
  };

  useEffect(() => {
    if (apiCallMade.current) return;
    apiCallMade.current = true;
    fetchCampaigns();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
      <div className={`p-3 sm:p-4 lg:p-6 ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className={`transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-red-800 to-red-900 shadow-lg">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className={`text-lg sm:text-xl lg:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Sub Partner Campaigns
                  </h2>
                  <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Manage and track your sub partner campaigns
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowAddModal(true)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-red-800 hover:bg-red-900 text-white shadow-lg' 
                    : 'bg-red-800 hover:bg-red-900 text-white shadow-lg'
                }`}
                style={{ backgroundColor: '#8b0000' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#7f1d1d'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#8b0000'}
              >
                <Plus className="h-4 w-4" />
                <span>Add Campaign</span>
              </button>
            </div>
          </div>

          {/* DataTable */}
          <div className={`transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '200ms' }}>
            <div className={`${isDarkMode ? 'bg-gradient-to-br from-black to-black border-gray-800' : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'} rounded-xl border shadow-lg overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={isDarkMode ? 'bg-black' : 'bg-gray-100'}>
                    <tr>
                      <th 
                        onClick={() => handleSort('campaignCode')}
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
                      >
                        <div className="flex items-center">
                          Campaign Code
                          {getSortIcon('campaignCode')}
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort('name')}
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
                      >
                        <div className="flex items-center">
                          Name
                          {getSortIcon('name')}
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort('creationDate')}
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
                      >
                        <div className="flex items-center">
                          Creation Date
                          {getSortIcon('creationDate')}
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort('clicks')}
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
                      >
                        <div className="flex items-center">
                          Clicks
                          {getSortIcon('clicks')}
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort('lots')}
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
                      >
                        <div className="flex items-center">
                          Lots
                          {getSortIcon('lots')}
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort('registrationAccounts')}
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
                      >
                        <div className="flex items-center">
                          Registration Accounts
                          {getSortIcon('registrationAccounts')}
                        </div>
                      </th>
                      <th 
                        onClick={() => handleSort('totalCommission')}
                        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
                      >
                        <div className="flex items-center">
                          Total Commission
                          {getSortIcon('totalCommission')}
                        </div>
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? 'divide-gray-800' : 'divide-gray-200'}`}>
                    {isLoading ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <Loader2 className={`h-5 w-5 animate-spin ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading campaigns...</span>
                          </div>
                        </td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center">
                          <span className="text-sm text-red-500">{error}</span>
                        </td>
                      </tr>
                    ) : campaigns.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center">
                          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>No campaigns found</span>
                        </td>
                      </tr>
                    ) : (
                      campaigns.map((campaign, index) => (
                        <tr 
                          key={campaign.campaignID}
                          className={`transition-colors duration-200 ${
                            isDarkMode 
                              ? 'hover:bg-black' 
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className={`px-4 py-3 whitespace-nowrap ${isDarkMode ? 'text-red-400' : 'text-red-800'}`}>
                            <div className="text-sm font-medium">{campaign.campaignCode}</div>
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            <div className="text-sm font-medium">{campaign.name}</div>
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <div className="flex items-center space-x-1">
                              <Calendar className={`h-4 w-4 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                              <span className="text-sm">{new Date(campaign.creationDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                            </div>
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <div className="flex items-center space-x-1">
                              <Eye className={`h-4 w-4 ${isDarkMode ? 'text-red-400' : 'text-red-800'}`} />
                              <span className="text-sm">{(campaign.clicks || 0).toLocaleString()}</span>
                            </div>
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <div className="flex items-center space-x-1">
                              <TrendingUp className={`h-4 w-4 ${isDarkMode ? 'text-red-400' : 'text-red-800'}`} />
                              <span className="text-sm">{(campaign.lots || 0).toLocaleString()}</span>
                            </div>
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <div className="flex items-center space-x-1">
                              <UserPlus className={`h-4 w-4 ${isDarkMode ? 'text-red-400' : 'text-red-800'}`} />
                              <span className="text-sm">{campaign.registrationAccounts || 0}</span>
                            </div>
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                            <div className="flex items-center space-x-1">
                              <DollarSign className="h-4 w-4" />
                              <span className="text-sm font-semibold">
                                ${(campaign.totalCommission || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium`}>
                            <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                setCampaignToEdit(campaign);
                                setEditCampaignName(campaign.name);
                                setShowEditModal(true);
                                setEditError('');
                              }}
                              className={`p-1.5 rounded transition-colors ${
                                isDarkMode 
                                  ? 'text-blue-400 hover:bg-blue-900/30' 
                                  : 'text-blue-600 hover:bg-blue-50'
                              }`}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setCampaignToDelete(campaign);
                                setShowDeleteModal(true);
                                setDeleteError('');
                              }}
                              className={`p-1.5 rounded transition-colors ${
                                isDarkMode 
                                  ? 'text-red-400 hover:bg-red-900/30' 
                                  : 'text-red-600 hover:bg-red-50'
                              }`}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Pagination Controls */}
            {!isLoading && campaigns.length > 0 && (
              <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 p-4 rounded-xl ${isDarkMode ? 'bg-black border border-gray-800' : 'bg-white border border-gray-200'}`}>
                <div className="flex items-center space-x-2">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Show</span>
                  <select
                    value={pagination.pageSize}
                    onChange={(e) => {
                      const newPageSize = parseInt(e.target.value);
                      setPagination(prev => ({ ...prev, pageSize: newPageSize, pageNumber: 1 }));
                      apiCallMade.current = false;
                      fetchCampaigns(1, newPageSize);
                    }}
                    className={`px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      isDarkMode 
                        ? 'bg-black border-gray-700 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>entries</span>
                </div>
                
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Showing {((pagination.pageNumber - 1) * pagination.pageSize) + 1} to {Math.min(pagination.pageNumber * pagination.pageSize, pagination.totalRecords)} of {pagination.totalRecords} entries
                </div>
                
                <div className="flex items-center space-x-1">
                  {/* Previous Button */}
                  <button
                    onClick={() => {
                      if (pagination.hasPreviousPage) {
                        fetchCampaigns(pagination.pageNumber - 1);
                      }
                    }}
                    disabled={!pagination.hasPreviousPage}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      pagination.hasPreviousPage
                        ? isDarkMode 
                          ? 'bg-gray-700 text-white hover:bg-gray-600' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : isDarkMode
                          ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Previous
                  </button>
                  
                  {/* Page Numbers */}
                  {(() => {
                    const pages = [];
                    const totalPages = pagination.totalPages;
                    const currentPage = pagination.pageNumber;
                    
                    if (totalPages > 0) {
                      pages.push(1);
                    }
                    
                    if (currentPage > 3) {
                      pages.push('...');
                    }
                    
                    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                      if (!pages.includes(i)) {
                        pages.push(i);
                      }
                    }
                    
                    if (currentPage < totalPages - 2) {
                      pages.push('...');
                    }
                    
                    if (totalPages > 1 && !pages.includes(totalPages)) {
                      pages.push(totalPages);
                    }
                    
                    return pages.map((page, index) => {
                      if (page === '...') {
                        return (
                          <span key={`ellipsis-${index}`} className={`px-2 py-1.5 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            ...
                          </span>
                        );
                      }
                      
                      return (
                        <button
                          key={page}
                          onClick={() => fetchCampaigns(page)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            page === currentPage
                              ? 'text-white'
                              : isDarkMode 
                                ? 'bg-gray-700 text-white hover:bg-gray-600' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          style={page === currentPage ? { backgroundColor: '#8b0000' } : {}}
                        >
                          {page}
                        </button>
                      );
                    });
                  })()}
                  
                  {/* Next Button */}
                  <button
                    onClick={() => {
                      if (pagination.hasNextPage) {
                        fetchCampaigns(pagination.pageNumber + 1);
                      }
                    }}
                    disabled={!pagination.hasNextPage}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      pagination.hasNextPage
                        ? isDarkMode 
                          ? 'bg-gray-700 text-white hover:bg-gray-600' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : isDarkMode
                          ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gradient-to-br from-black to-black border border-gray-700' : 'bg-white'} rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-xl bg-gradient-to-br from-red-600 to-red-700 shadow-lg">
                    <Trash2 className="h-5 w-5 text-white" />
                  </div>
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Delete Campaign
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setCampaignToDelete(null);
                    setDeleteError('');
                  }}
                  className={`p-2 rounded-lg hover:bg-gray-100 ${isDarkMode ? 'hover:bg-black' : 'hover:bg-gray-100'}`}
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Are you sure you want to delete the campaign <strong>"{campaignToDelete?.name}"</strong>? This action cannot be undone.
              </p>

              {deleteError && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 text-sm mb-4">
                  {deleteError}
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setCampaignToDelete(null);
                    setDeleteError('');
                  }}
                  disabled={isDeleting}
                  className={`flex-1 px-4 py-2.5 rounded-lg border transition-all duration-300 ${
                    isDarkMode 
                      ? 'border-gray-600 text-white hover:bg-black' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  } ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteCampaign}
                  disabled={isDeleting}
                  className={`flex-1 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 ${isDeleting ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <span>Delete</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Campaign Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gradient-to-br from-black to-black border border-gray-700' : 'bg-white'} rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-xl bg-gradient-to-br from-red-800 to-red-900 shadow-lg">
                    <Edit className="h-5 w-5 text-white" />
                  </div>
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Edit Campaign
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setCampaignToEdit(null);
                    setEditCampaignName('');
                    setEditError('');
                  }}
                  className={`p-2 rounded-lg hover:bg-gray-100 ${isDarkMode ? 'hover:bg-black' : 'hover:bg-gray-100'}`}
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (editCampaignName.trim() && campaignToEdit) {
                    setIsEditing(true);
                    setEditError('');
                    try {
                      const response = await apiService.updateSubCampaign(campaignToEdit.campaignID, editCampaignName.trim());
                      if (response.success) {
                        setShowEditModal(false);
                        setCampaignToEdit(null);
                        setEditCampaignName('');
                        fetchCampaigns();
                      } else {
                        setEditError(response.message || 'Failed to update campaign');
                      }
                    } catch (error) {
                      setEditError(error.message || 'Failed to update campaign');
                    } finally {
                      setIsEditing(false);
                    }
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Campaign Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editCampaignName}
                    onChange={(e) => setEditCampaignName(e.target.value)}
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-black border-gray-800 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                    placeholder="Enter campaign name"
                    required
                    autoFocus
                  />
                </div>

                {editError && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 text-sm">
                    {editError}
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setCampaignToEdit(null);
                      setEditCampaignName('');
                      setEditError('');
                    }}
                    disabled={isEditing}
                    className={`flex-1 px-4 py-2.5 rounded-lg border transition-all duration-300 ${
                      isDarkMode 
                        ? 'border-gray-600 text-white hover:bg-black' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    } ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isEditing}
                    className={`flex-1 px-4 py-2.5 rounded-lg text-white transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 ${isEditing ? 'opacity-70 cursor-not-allowed' : ''}`}
                    style={{ backgroundColor: '#8b0000' }}
                    onMouseEnter={(e) => !isEditing && (e.target.style.backgroundColor = '#7f1d1d')}
                    onMouseLeave={(e) => !isEditing && (e.target.style.backgroundColor = '#8b0000')}
                  >
                    {isEditing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      <span>Update Campaign</span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Campaign Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gradient-to-br from-black to-black border border-gray-700' : 'bg-white'} rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-xl bg-gradient-to-br from-red-800 to-red-900 shadow-lg">
                    <Plus className="h-5 w-5 text-white" />
                  </div>
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Add New Campaign
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setCampaignName('');
                    setSubmitError('');
                  }}
                  className={`p-2 rounded-lg hover:bg-gray-100 ${isDarkMode ? 'hover:bg-black' : 'hover:bg-gray-100'}`}
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (campaignName.trim()) {
                    setIsSubmitting(true);
                    setSubmitError('');
                    try {
                      const response = await apiService.createSubCampaign(campaignName.trim());
                      if (response.success) {
                        setShowAddModal(false);
                        setCampaignName('');
                        fetchCampaigns();
                      } else {
                        setSubmitError(response.message || 'Failed to create campaign');
                      }
                    } catch (error) {
                      setSubmitError(error.message || 'Failed to create campaign');
                    } finally {
                      setIsSubmitting(false);
                    }
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Campaign Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-black border-gray-800 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                    placeholder="Enter campaign name"
                    required
                    autoFocus
                  />
                </div>

                {submitError && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 text-sm">
                    {submitError}
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setCampaignName('');
                      setSubmitError('');
                    }}
                    disabled={isSubmitting}
                    className={`flex-1 px-4 py-2.5 rounded-lg border transition-all duration-300 ${
                      isDarkMode 
                        ? 'border-gray-600 text-white hover:bg-black' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`flex-1 px-4 py-2.5 rounded-lg text-white transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    style={{ backgroundColor: '#8b0000' }}
                    onMouseEnter={(e) => !isSubmitting && (e.target.style.backgroundColor = '#7f1d1d')}
                    onMouseLeave={(e) => !isSubmitting && (e.target.style.backgroundColor = '#8b0000')}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Creating...</span>
                      </>
                    ) : (
                      <span>Add Campaign</span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubAffiliateCampaigns;
